import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';

import { Box, Button, Stack, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

import { OrdersViewToggle } from './calendar/OrdersViewToggle';

import type {
  JizdaListItem,
  JizdaResponse,
  JizdySortBy,
} from '@/api/types/Jizda';
import { Roles } from '@/constants/roles';
import { NEW_ORDER_ID } from '@/constants/orders';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  loadOrdersPage,
  removeOrder,
  selectOrders,
  selectOrdersFilters,
  selectOrdersHasMore,
  selectOrdersStatus,
  setFilters,
  upsertOrder,
} from '@/store/ordersSlice';
import { selectUserRoles } from '@/store/userSlice';

import { AuditHistoryModal } from '@/components/audit/AuditHistoryModal';

import { OrderDetailPanel } from './OrderDetailPanel';
import { OrderEditor } from './OrderEditor';
import { OrdersCards } from './OrdersCards';
import { OrdersExport } from './OrdersExport';
import { OrdersFilters } from './OrdersFilters';
import { OrdersTable } from './OrdersTable';


// Reduce a JizdaResponse (full DTO) into the trimmed JizdaListItem (overview
// row) shape so we can upsert it into the list state after an editor save
// without a round-trip reload. `noteForDispatcherSnippet` is derived locally;
// the api recomputes it on the next list reload anyway.
const NOTE_SNIPPET_MAX = 120;

const toListItem = (o: JizdaResponse): JizdaListItem => ({
  id: o.id,
  version: o.version,
  createdAt: o.createdAt,
  customer: o.customer,
  commodity: o.commodity,
  loadingPlace: o.loadingPlace,
  unloadingPlace: o.unloadingPlace,
  loadingDate: o.loadingDate,
  plannedArrival: o.plannedArrival,
  vehicle: o.vehicle,
  carrier: o.carrier,
  carrierType: o.carrierType,
  assignedDispatcherSub: o.assignedDispatcherSub,
  noteForDispatcherSnippet: o.noteForDispatcher
    ? o.noteForDispatcher.slice(0, NOTE_SNIPPET_MAX)
    : null,
  deleted: o.deleted,
  stornoState: o.stornoState,
  revisionCount: o.revisionCount,
});

export const OrdersPage = (): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('orders');
  const dispatch = useAppDispatch();
  // Compact threshold matches MUI v7 `md` (900px) — same value used by
  // Topbar / mockup desktop ↔ mobile split. Picking `md` (not `sm`) keeps
  // tablets on the desktop table view, which the mockup is also styled for.
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  const orders = useAppSelector(selectOrders);
  const filters = useAppSelector(selectOrdersFilters);
  const hasMore = useAppSelector(selectOrdersHasMore);
  const status = useAppSelector(selectOrdersStatus);
  const myRoles = useAppSelector(selectUserRoles);
  const isBackoffice = myRoles.includes(Roles.BACKOFFICE);

  // `editorOrderId` drives the MODAL editor: used for mobile row-tap and for
  // "Nová objednávka" (new order) on both layouts. On desktop, an existing
  // order row-click instead targets the right-side panel via `selectedOrderId`.
  const [editorOrderId, setEditorOrderId] = useState<string | null>(null);
  const [historyOrderId, setHistoryOrderId] = useState<string | null>(null);
  // Desktop master-detail state. `selectedOrderId` → right panel target
  // (null → empty-state placeholder). CHR-02: the accordion is gone (flat
  // jízdy), so there is no expanded-rows state.
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  // First load on mount + reload whenever filters change.
  useEffect(() => {
    void dispatch(loadOrdersPage({ reset: true }));
  }, [dispatch, filters]);

  // Mobile default sort = loadingDate ASC (CHR-04 / ADR-0043 decision D). The
  // server default stays `createdAt`; the mobile cards view passes
  // `sortBy=loadingDate&sortDir=asc` as a client query param. Applied once on
  // entering the compact (mobile/tablet) layout, only while the sort is still
  // the untouched server default, so a user who has explicitly sorted is not
  // overridden. The desktop table keeps the `createdAt` default.
  useEffect(() => {
    if (isCompact && filters.sortBy === 'createdAt' && filters.sortDir === 'asc') {
      dispatch(setFilters({ sortBy: 'loadingDate', sortDir: 'asc' }));
    }
  }, [dispatch, isCompact, filters.sortBy, filters.sortDir]);

  const handleLoadMore = useCallback(() => {
    if (status === 'loading' || status === 'loadingMore') return;
    void dispatch(loadOrdersPage({ reset: false }));
  }, [dispatch, status]);

  const handleFiltersChange = useCallback(
    (next: Partial<typeof filters>) => {
      dispatch(setFilters(next));
    },
    [dispatch],
  );

  // Mobile (OrdersCards): row-tap opens the MODAL editor (unchanged).
  const handleOpenOrder = useCallback((id: string) => setEditorOrderId(id), []);
  // Desktop (OrdersTable): row-click selects the order into the right panel
  // (NO modal). The editable form lives in OrderDetailPanel.
  const handleSelectOrder = useCallback(
    (id: string) => setSelectedOrderId(id),
    [],
  );
  const handleClosePanel = useCallback(() => setSelectedOrderId(null), []);
  const handleOpenHistory = useCallback((id: string) => setHistoryOrderId(id), []);

  // Sort toggle wired to the OrdersTable header. First click on a column
  // sets `asc`; same column re-clicked toggles `asc ↔ desc`. Switching
  // columns resets direction to `asc`. Cursor is reset by the
  // `useEffect([filters])` above (filters object identity changes).
  const handleSortChange = useCallback(
    (nextSortBy: JizdySortBy) => {
      if (filters.sortBy === nextSortBy) {
        dispatch(
          setFilters({ sortDir: filters.sortDir === 'asc' ? 'desc' : 'asc' }),
        );
      } else {
        dispatch(setFilters({ sortBy: nextSortBy, sortDir: 'asc' }));
      }
    },
    [dispatch, filters.sortBy, filters.sortDir],
  );

  const handleSaved = useCallback(
    (saved: JizdaResponse) => {
      dispatch(upsertOrder(toListItem(saved)));
    },
    [dispatch],
  );

  const handleDeleted = useCallback(
    (id: string) => {
      // A delete clears the panel selection (the order is gone / now hidden).
      setSelectedOrderId((prev) => (prev === id ? null : prev));
      if (!filters.showDeleted) {
        dispatch(removeOrder(id));
      } else {
        // Will be re-rendered as deleted=true on next list refresh; for now
        // we trigger one explicitly so the row visually flips state.
        void dispatch(loadOrdersPage({ reset: true }));
      }
    },
    [dispatch, filters.showDeleted],
  );

  // Resolve the selected order's list item for the panel header. Falls back to
  // null (empty-state) if the selection no longer exists in the loaded page.
  const selectedOrder = useMemo(
    () =>
      selectedOrderId !== null
        ? (orders.find((o) => o.id === selectedOrderId) ?? null)
        : null,
    [orders, selectedOrderId],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        // DESKTOP (CHR-01 independent-scroll): the page itself does NOT grow
        // or scroll — the toolbar + filters stay fixed and the two
        // master-detail columns own their own scrollbars below.
        // `overflow:hidden` here is the ceiling that forces that. MOBILE keeps
        // natural scroll inside `main` (OrdersCards lazy-loads as the page
        // scrolls) — so no clamp there.
        overflow: isCompact ? undefined : 'hidden',
      }}
    >
      {/* Dedicated toolbar row (change-spec item 3): the Seznam|Kalendář switch
          lives on its own row — left-aligned — with the write actions pushed
          right. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ pb: theme.spacing(2), flexWrap: 'wrap', gap: theme.spacing(1.5) }}
      >
        {/* Seznam | Kalendář view switch (CHR-03 §6 / ADR-0051) — visible to
            every role; this page is the "Seznam" view. */}
        <OrdersViewToggle value="list" />
        {isBackoffice ? (
          <Stack direction="row" gap={theme.spacing(1.5)} alignItems="center">
            {/* Export — backoffice-only, placed LEFT of "Nová jízda"
                (CHR-03): opens a modal date-range + format export dialog. */}
            <OrdersExport />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setEditorOrderId(NEW_ORDER_ID)}
              data-testid="new-order-button"
            >
              {t('newOrder')}
            </Button>
          </Stack>
        ) : null}
      </Stack>
      <OrdersFilters filters={filters} onChange={handleFiltersChange} />
      {isCompact ? (
        // MOBILE — unchanged: OrdersCards + modal editor. Hard requirement.
        <OrdersCards
          orders={orders}
          hasMore={hasMore}
          loadingMore={status === 'loadingMore' || status === 'loading'}
          onLoadMore={handleLoadMore}
          onOpenOrder={handleOpenOrder}
          onOpenHistory={handleOpenHistory}
        />
      ) : (
        // DESKTOP — in-page master-detail (ADR-0033): 2/3 table : 1/3 panel.
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            minHeight: 0,
            gap: theme.spacing(2),
          }}
        >
          {/* Master (~2/3). minWidth:0 lets the table shrink without clipping. */}
          <Box
            sx={{
              flexBasis: '66.6%',
              flexGrow: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <OrdersTable
              orders={orders}
              hasMore={hasMore}
              loading={status === 'loading'}
              loadingMore={status === 'loadingMore'}
              sortBy={filters.sortBy}
              sortDir={filters.sortDir}
              onSortChange={handleSortChange}
              onLoadMore={handleLoadMore}
              onOpenOrder={handleSelectOrder}
              selectedOrderId={selectedOrderId}
            />
          </Box>
          {/* Detail (~1/3). Independent own scroll (CHR-01), minWidth floor. */}
          <Box
            sx={{
              flexBasis: '33.3%',
              flexGrow: 0,
              flexShrink: 0,
              minWidth: 360,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <OrderDetailPanel
              order={selectedOrder}
              onClose={handleClosePanel}
              onSaved={handleSaved}
              onDeleted={handleDeleted}
              onOpenHistory={handleOpenHistory}
            />
          </Box>
        </Box>
      )}
      {/* Modal editor — mobile row-tap + new-order on both layouts. */}
      <OrderEditor
        orderId={editorOrderId}
        onClose={() => setEditorOrderId(null)}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
      <AuditHistoryModal
        entityKind="jizda"
        entityId={historyOrderId}
        onClose={() => setHistoryOrderId(null)}
      />
    </Box>
  );
};
